import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { GeoUnitSelect } from '@/components/common/GeoUnitSelect';
import { OptionSelect } from '@/components/common/OptionSelect';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { activeInstituteTypesQueryOptions } from '@/features/instituteTypes/api/instituteTypes.api';
import { bookTypesQueryOptions } from '@/features/product/api/product.api';
import { RequisitionInstituteSelect } from './RequisitionInstituteSelect';
import { useSaveDonationRequisition } from '../hooks/useMpP2Mutations';
import type { DonationRequisition } from '../types/marketPromotionP2.types';

interface DonationRequisitionFormDialogProps {
  requisition: DonationRequisition | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface FormState {
  instituteId: number | undefined;
  instituteTypeNo: number | undefined;
  bookTypeNo: number | undefined;
  thanaId: number | undefined;
  teacherName: string;
  teacherPhone: string;
  budgetAmount: string;
  targetSaleQty: string;
  studentQty: string;
}

const EMPTY: FormState = {
  instituteId: undefined,
  instituteTypeNo: undefined,
  bookTypeNo: undefined,
  thanaId: undefined,
  teacherName: '',
  teacherPhone: '',
  budgetAmount: '',
  targetSaleQty: '',
  studentQty: '',
};

const BOOK_TYPE_LOOKUP = { page: 1, pageSize: 200, isActive: true } as const;

export function DonationRequisitionFormDialog({
  requisition,
  open,
  onOpenChange,
}: DonationRequisitionFormDialogProps) {
  const [form, setForm] = useState<FormState>(() =>
    requisition === null
      ? EMPTY
      : {
          instituteId: requisition.instituteId ?? undefined,
          instituteTypeNo: undefined,
          bookTypeNo: requisition.bookTypeNo ?? undefined,
          thanaId: undefined,
          teacherName: requisition.teacherName ?? '',
          teacherPhone: requisition.teacherPhone ?? '',
          budgetAmount: String(requisition.budgetAmount),
          targetSaleQty: String(requisition.targetSaleQty),
          studentQty: requisition.studentQty === null ? '' : String(requisition.studentQty),
        },
  );

  const { data: instituteTypes } = useQuery(activeInstituteTypesQueryOptions());
  const { data: bookTypes } = useQuery(bookTypesQueryOptions(BOOK_TYPE_LOOKUP));

  const save = useSaveDonationRequisition(() => onOpenChange(false));

  const patch = (next: Partial<FormState>) => setForm((prev) => ({ ...prev, ...next }));

  const ready = form.budgetAmount.trim() !== '';

  function submit() {
    save.mutate({
      id: requisition?.id ?? null,
      payload: {
        ...(form.instituteTypeNo === undefined ? {} : { instituteTypeNo: form.instituteTypeNo }),
        ...(form.bookTypeNo === undefined ? {} : { bookTypeNo: form.bookTypeNo }),
        ...(form.thanaId === undefined ? {} : { thanaId: form.thanaId }),
        ...(form.teacherName.trim() === '' ? {} : { teacherName: form.teacherName.trim() }),
        ...(form.teacherPhone.trim() === '' ? {} : { teacherPhone: form.teacherPhone.trim() }),
        budgetAmount: Number(form.budgetAmount),
        targetSaleQty: form.targetSaleQty.trim() === '' ? 0 : Number(form.targetSaleQty),
        ...(form.studentQty.trim() === '' ? {} : { studentQty: Number(form.studentQty) }),
      },
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <div className="min-w-0">
          <DialogHeader>
            <DialogTitle>
              {requisition === null ? 'Raise a Requisition' : 'Edit Requisition'}
            </DialogTitle>
            <DialogDescription>
              What is asked for against an institute before a donation is given.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="mpr-inst-type">Institute Type</Label>
              <OptionSelect
                id="mpr-inst-type"
                options={(instituteTypes ?? []).map((row) => ({ id: row.id, name: row.name }))}
                value={form.instituteTypeNo}
                onChange={(instituteTypeNo) => patch({ instituteTypeNo })}
                placeholder="Any type"
                clearable
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="mpr-book-type">Book Type</Label>
              <OptionSelect
                id="mpr-book-type"
                options={(bookTypes?.data ?? []).map((row) => ({
                  id: row.legacyId ?? row.id,
                  name: row.name,
                }))}
                value={form.bookTypeNo}
                onChange={(bookTypeNo) => patch({ bookTypeNo })}
                placeholder="Any book type"
                clearable
              />
            </div>

            <div className="flex flex-col gap-1.5 sm:col-span-2">
              <Label>Thana</Label>
              <GeoUnitSelect
                value={form.thanaId}
                onChange={(thanaId) => patch({ thanaId, instituteId: undefined })}
              />
            </div>

            <div className="flex flex-col gap-1.5 sm:col-span-2">
              <Label htmlFor="mpr-institute">Institute</Label>
              <RequisitionInstituteSelect
                id="mpr-institute"
                thanaId={form.thanaId}
                value={form.instituteId}
                onChange={(instituteId) => patch({ instituteId })}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="mpr-teacher">Teacher Name</Label>
              <Input
                id="mpr-teacher"
                value={form.teacherName}
                onChange={(event) => patch({ teacherName: event.target.value })}
                placeholder="Md. Karim"
                autoComplete="off"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="mpr-phone">Teacher Phone</Label>
              <Input
                id="mpr-phone"
                value={form.teacherPhone}
                onChange={(event) => patch({ teacherPhone: event.target.value })}
                placeholder="01XXXXXXXXX"
                autoComplete="off"
                className="tabular-nums"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="mpr-students">Students</Label>
              <Input
                id="mpr-students"
                value={form.studentQty}
                onChange={(event) => patch({ studentQty: event.target.value })}
                placeholder="300"
                inputMode="numeric"
                autoComplete="off"
                className="tabular-nums"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="mpr-target">Target Sale</Label>
              <Input
                id="mpr-target"
                value={form.targetSaleQty}
                onChange={(event) => patch({ targetSaleQty: event.target.value })}
                placeholder="100"
                inputMode="numeric"
                autoComplete="off"
                className="tabular-nums"
              />
            </div>

            <div className="flex flex-col gap-1.5 sm:col-span-2">
              <Label htmlFor="mpr-budget">Budget Asked For</Label>
              <Input
                id="mpr-budget"
                value={form.budgetAmount}
                onChange={(event) => patch({ budgetAmount: event.target.value })}
                placeholder="5000"
                inputMode="numeric"
                autoComplete="off"
                className="tabular-nums"
              />
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={submit} disabled={save.isPending || !ready}>
              {save.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
              {requisition === null ? 'Create' : 'Update'}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
